import React, { useState, useEffect } from 'react';
import {
  Check, X, Star, Zap, Building2, Cpu, ArrowRight,
  ChevronDown, ChevronUp, Loader2, CircleDot, ShoppingCart,
  TrendingDown, Users, BadgeDollarSign, ShieldAlert, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_ICONS = {
  small_business: Star,
  full_business: Building2,
  enterprise: Zap,
  complete: Cpu,
};

const TIER_COLORS = {
  small_business: { card: 'border-sky-300 shadow-sky-100', header: 'bg-sky-600', badge: 'bg-amber-400 text-amber-900', accent: 'text-sky-600', btn: 'bg-sky-600 hover:bg-sky-700' },
  full_business:  { card: 'border-emerald-400 shadow-emerald-100', header: 'bg-emerald-700', badge: 'bg-emerald-400 text-emerald-900', accent: 'text-emerald-700', btn: 'bg-emerald-700 hover:bg-emerald-800' },
  enterprise:     { card: 'border-indigo-300 shadow-indigo-100', header: 'bg-indigo-700', badge: '', accent: 'text-indigo-700', btn: 'bg-indigo-700 hover:bg-indigo-800' },
  complete:       { card: 'border-amber-300 shadow-amber-100', header: 'bg-slate-800', badge: 'bg-amber-400 text-amber-900', accent: 'text-slate-800', btn: 'bg-slate-800 hover:bg-slate-900' },
};

const PricingPage = () => {
  const [tiers, setTiers] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [billing, setBilling] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [showComparison, setShowComparison] = useState(false);
  const [showCompetitors, setShowCompetitors] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);
  const [expandedTier, setExpandedTier] = useState(null);

  useEffect(() => { fetchTiers(); fetchComparison(); }, []);

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/tiers`);
      if (res.ok) setTiers((await res.json()).tiers || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchComparison = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/comparison`);
      if (res.ok) setComparison(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleSubscribe = async (tierId) => {
    setCheckingOut(tierId);
    try {
      const res = await fetch(`${API_URL}/api/pricing/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier_id: tierId, billing_cycle: billing, addons: [], origin_url: window.location.origin }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) { window.location.href = data.url; return; }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Failed to create checkout');
    } catch { toast.error('Failed to initiate payment'); }
    finally { setCheckingOut(null); }
  };

  const getPrice = (tier) => billing === 'annual' ? tier.price_annual : tier.price_monthly;
  const getPeriod = () => billing === 'annual' ? '/yr' : '/mo';

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-sky-600" /></div>;

  return (
    <div className="space-y-10 max-w-7xl mx-auto">

      {/* Hero Header */}
      <div className="text-center space-y-3">
        <h1 data-testid="pricing-page-title" className="text-3xl font-bold text-slate-800">
          Digital Blue Skies Accounting
        </h1>
        <p className="text-slate-500 max-w-3xl mx-auto text-sm leading-relaxed">
          The only accounting system that bundles payroll with zero per-employee fees.
          Competitors charge $50-180/mo extra for payroll, then add $6-22 per employee on top.
          We don&apos;t.
        </p>

        {/* Key differentiators strip */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-xs">
          <span className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full font-medium">
            <Check className="w-3.5 h-3.5" /> Payroll Bundled — Not Extra
          </span>
          <span className="flex items-center gap-1.5 bg-sky-50 text-sky-700 px-3 py-1.5 rounded-full font-medium">
            <Users className="w-3.5 h-3.5" /> No Per-Employee Fees
          </span>
          <span className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full font-medium">
            <BadgeDollarSign className="w-3.5 h-3.5" /> Save $1,500+/yr vs QuickBooks
          </span>
          <span className="flex items-center gap-1.5 bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-full font-medium">
            <ShieldAlert className="w-3.5 h-3.5" /> IRS/SSA + FLC Compliance Built-In
          </span>
        </div>

        {/* Billing Toggle */}
        <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1 mt-5" data-testid="billing-toggle">
          <button onClick={() => setBilling('monthly')} className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${billing === 'monthly' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
            Monthly
          </button>
          <button onClick={() => setBilling('annual')} className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${billing === 'annual' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
            Annual <span className="text-emerald-600 text-xs ml-1">Save ~15%</span>
          </button>
        </div>
      </div>

      {/* Tier Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="tier-cards-grid">
        {tiers.map((tier) => {
          const colors = TIER_COLORS[tier.id] || TIER_COLORS.small_business;
          const Icon = TIER_ICONS[tier.id] || Star;
          const isExpanded = expandedTier === tier.id;
          const cc = tier.competitor_comparison || {};

          return (
            <Card key={tier.id} data-testid={`tier-card-${tier.id}`}
              className={`relative flex flex-col border-2 ${colors.card} transition-all hover:shadow-lg`}>

              {tier.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-bold ${colors.badge}`}>
                  {tier.badge}
                </div>
              )}

              <div className={`${colors.header} text-white p-5 rounded-t-lg`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-5 h-5" />
                  <h3 className="font-bold text-lg">{tier.name}</h3>
                </div>
                <p className="text-white/80 text-xs leading-snug">{tier.positioning}</p>
                <div className="mt-3 flex items-end gap-1">
                  <span className="text-3xl font-bold">${getPrice(tier).toLocaleString()}</span>
                  <span className="text-white/60 text-sm mb-0.5">{getPeriod()}</span>
                </div>
                {tier.savings_note && (
                  <p className="mt-1.5 text-xs text-white/90 bg-white/15 rounded px-2 py-0.5 inline-block">
                    <TrendingDown className="w-3 h-3 inline mr-1" />{tier.savings_note}
                  </p>
                )}
              </div>

              <CardContent className="flex-1 p-4 space-y-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Included</p>
                <ul className="space-y-1.5">
                  {tier.core_features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* Competitor callouts (always visible, compact) */}
                {Object.keys(cc).length > 0 && (
                  <div className="border-t border-dashed border-slate-200 pt-2 space-y-1.5">
                    <p className="text-xs font-semibold text-red-400 uppercase tracking-wider">Why switch from...</p>
                    {Object.entries(cc).slice(0, 2).map(([key, c]) => (
                      <div key={key} className="bg-red-50/60 rounded p-2 text-xs">
                        <span className="font-semibold text-slate-700">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: </span>
                        <span className="text-red-600 font-medium">{c.price || c.total_10emp}</span>
                        {c.lacks && <span className="text-slate-500 block mt-0.5">{c.lacks.substring(0, 80)}{c.lacks.length > 80 ? '...' : ''}</span>}
                      </div>
                    ))}
                  </div>
                )}

                {isExpanded && (
                  <div className="pt-2 border-t border-slate-100 space-y-2">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Key Value</p>
                    <ul className="space-y-1">
                      {tier.value_points.map((v, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                          <CircleDot className="w-3 h-3 mt-0.5 shrink-0 text-emerald-400" />
                          <span>{v}</span>
                        </li>
                      ))}
                    </ul>
                    {tier.addon_details?.length > 0 && (
                      <>
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider pt-1">Optional Add-Ons</p>
                        <ul className="space-y-1">
                          {tier.addon_details.map((a) => (
                            <li key={a.code} className="flex items-center justify-between text-xs text-slate-500">
                              <span>{a.name}</span>
                              <span className="font-medium text-slate-600">+${billing === 'annual' ? a.price_annual : a.price_monthly}{getPeriod()}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {Object.entries(cc).slice(2).map(([key, c]) => (
                      <div key={key} className="bg-red-50/60 rounded p-2 text-xs">
                        <span className="font-semibold text-slate-700">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: </span>
                        <span className="text-red-600 font-medium">{c.price || c.total_10emp}</span>
                        {c.lacks && <span className="text-slate-500 block mt-0.5">{c.lacks}</span>}
                      </div>
                    ))}
                    <p className="text-xs text-slate-400"><span className="font-medium">Target:</span> {tier.target}</p>
                  </div>
                )}

                <button onClick={() => setExpandedTier(isExpanded ? null : tier.id)} className="flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800 transition-colors" data-testid={`toggle-details-${tier.id}`}>
                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {isExpanded ? 'Less details' : 'More details & comparisons'}
                </button>
              </CardContent>

              <div className="p-4 pt-0">
                <Button data-testid={`subscribe-btn-${tier.id}`} className={`w-full ${colors.btn} text-white`}
                  onClick={() => handleSubscribe(tier.id)} disabled={!!checkingOut}>
                  {checkingOut === tier.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
                  {checkingOut === tier.id ? 'Redirecting...' : 'Subscribe Now'}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Savings Calculator Banner */}
      {comparison?.savings_10emp && (
        <Card className="bg-gradient-to-r from-emerald-600 to-sky-600 text-white border-0" data-testid="savings-banner">
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-1">Real Savings — {comparison.savings_10emp.scenario}</h3>
            <p className="text-white/80 text-xs mb-4">Based on published competitor pricing (May 2026). No per-employee fees with DBSA.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {comparison.savings_10emp.comparisons.map((c, i) => (
                <div key={i} className="bg-white/15 backdrop-blur-sm rounded-lg p-3 text-center">
                  <p className="text-xs text-white/70 mb-1">vs {c.competitor}</p>
                  <p className="text-xs line-through text-white/50">{c.their_price}</p>
                  <p className="font-bold text-lg">{c.dbsa_price}</p>
                  <p className="text-xs mt-1 bg-white/20 rounded px-2 py-0.5 inline-block font-semibold">
                    Save {c.annual_savings}/yr
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feature Comparison */}
      <div className="space-y-3">
        <button data-testid="toggle-comparison-btn" onClick={() => setShowComparison(!showComparison)}
          className="flex items-center gap-2 mx-auto text-sm font-medium text-sky-600 hover:text-sky-800 transition-colors">
          {showComparison ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {showComparison ? 'Hide' : 'Show'} Full Feature Comparison
        </button>

        {showComparison && comparison && (
          <Card data-testid="feature-comparison-table">
            <CardHeader className="pb-2"><CardTitle className="text-lg text-slate-700">Feature Comparison</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="text-left p-2 text-slate-600 font-semibold w-1/3">Feature</th>
                    <th className="text-center p-2 text-sky-700 font-semibold">Small Business<br/><span className="text-xs font-normal">${billing === 'annual' ? '399/yr' : '39/mo'}</span></th>
                    <th className="text-center p-2 text-emerald-700 font-semibold">Full Business<br/><span className="text-xs font-normal">${billing === 'annual' ? '999/yr' : '99/mo'}</span></th>
                    <th className="text-center p-2 text-indigo-700 font-semibold">Enterprise<br/><span className="text-xs font-normal">${billing === 'annual' ? '2,499/yr' : '249/mo'}</span></th>
                    <th className="text-center p-2 text-slate-700 font-semibold">Scan & Complete<br/><span className="text-xs font-normal">${billing === 'annual' ? '4,999/yr' : '499/mo'}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.feature_matrix.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-slate-50/50' : ''}>
                      <td className="p-2 text-slate-700 font-medium">{row.feature}</td>
                      {['small_business', 'full_business', 'enterprise', 'complete'].map((tid) => (
                        <td key={tid} className="text-center p-2">
                          {row[tid] === true ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : row[tid] === 'addon' ? <span className="text-xs text-amber-600 font-medium">Add-on</span> : <X className="w-4 h-4 text-slate-300 mx-auto" />}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Competitor Landscape */}
      <div className="space-y-3">
        <button data-testid="toggle-competitors-btn" onClick={() => setShowCompetitors(!showCompetitors)}
          className="flex items-center gap-2 mx-auto text-sm font-medium text-red-500 hover:text-red-700 transition-colors">
          {showCompetitors ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {showCompetitors ? 'Hide' : 'Show'} Competitor Analysis
        </button>

        {showCompetitors && comparison?.competitors && (
          <div className="space-y-4">
            {comparison.competitors.map((comp, ci) => (
              <Card key={ci} className="border-red-100">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-slate-700 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" /> {comp.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {comp.tiers.map((t, ti) => (
                      <div key={ti} className="bg-slate-50 rounded-lg p-2.5 text-xs">
                        <p className="font-semibold text-slate-700">{t.name}</p>
                        <p className="text-red-600 font-bold text-sm">{t.price}</p>
                        <p className="text-slate-400 mt-0.5">{t.users}</p>
                        <p className="text-slate-400">Payroll: {t.payroll}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-red-500 mb-1">Weaknesses vs DBSA:</p>
                    <ul className="space-y-0.5">
                      {comp.weaknesses.map((w, wi) => (
                        <li key={wi} className="flex items-start gap-1.5 text-xs text-slate-600">
                          <X className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <Card className="bg-slate-800 text-white border-0">
        <CardContent className="p-6 text-center space-y-3">
          <h3 className="font-bold text-xl">Stop paying per-employee fees.</h3>
          <p className="text-slate-300 text-sm max-w-2xl mx-auto">
            QuickBooks, Gusto, and ADP all charge you more as you grow. DBSA gives you a flat rate — 
            full accounting, full payroll, full compliance. Whether you have 5 employees or 500.
          </p>
          <Button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-emerald-500 hover:bg-emerald-600 text-white mt-2">
            <ArrowRight className="w-4 h-4 mr-2" /> Choose Your Plan
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PricingPage;
